import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { Icon } from '@noths/polaris-design-system-core-components';
import Link from 'next/link';
import { useRouter } from 'next/router';

import * as styles from './Toggle.styles';

export interface ToggleProps {
  active: boolean;
  boldText?: boolean;
  crawlable?: boolean;
  disabled?: boolean;
  href?: string;
  label: string;
  name: string;
  onToggle?: (currentActive: boolean) => void;
  secondaryLabelText?: string;
  value: string;
}

interface CrawlableLinkProps {
  accessibleName: string;
  active: boolean;
  href: string;
  isNavigationOnly: boolean;
  testId: string;
}

const CrawlableLink = ({
  accessibleName,
  active,
  href,
  isNavigationOnly,
  testId,
}: CrawlableLinkProps) => (
  <Link
    aria-current={isNavigationOnly && active ? 'true' : undefined}
    aria-hidden={isNavigationOnly ? undefined : 'true'}
    css={[styles.crawlLinkUnderlay, !isNavigationOnly && styles.crawlOnlyUnderlay]}
    data-testid={testId}
    href={href}
    prefetch={false}
    scroll={false}
    shallow
    tabIndex={isNavigationOnly ? undefined : -1}
  >
    <span css={visuallyHidden}>{accessibleName}</span>
  </Link>
);

interface NonCrawlableButtonProps {
  accessibleName: string;
  active: boolean;
  href: string;
  testId: string;
}

const NonCrawlableButton = ({ accessibleName, active, href, testId }: NonCrawlableButtonProps) => {
  const router = useRouter();
  return (
    <button
      aria-current={active ? 'true' : undefined}
      css={styles.crawlLinkUnderlay}
      data-href={href}
      data-testid={testId}
      onClick={() => router.push(href, undefined, { shallow: true, scroll: false })}
      type="button"
    >
      <span css={visuallyHidden}>{accessibleName}</span>
    </button>
  );
};

export const Toggle = ({
  active,
  boldText,
  crawlable,
  disabled,
  href,
  label: labelText,
  name,
  onToggle,
  secondaryLabelText,
  value,
}: ToggleProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onToggle) {
      onToggle(event.target.checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (disabled || !onToggle) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(!active);
    }
  };

  const outerStyle = [styles.toggleItem, disabled && styles.disabled];
  const hasNavigableHref = Boolean(href && !disabled);
  const isNavigationOnly = hasNavigableHref && !onToggle;

  const accessibleName = `${labelText}${secondaryLabelText ? ` ${secondaryLabelText}` : ''}`;

  let navigationElement: React.ReactNode = null;
  if (hasNavigableHref && crawlable) {
    navigationElement = (
      <CrawlableLink
        accessibleName={accessibleName}
        active={active}
        href={href!}
        isNavigationOnly={isNavigationOnly}
        testId={`${name}-${value}-link`}
      />
    );
  } else if (isNavigationOnly) {
    navigationElement = (
      <NonCrawlableButton
        accessibleName={accessibleName}
        active={active}
        href={href!}
        testId={`${name}-${value}-button`}
      />
    );
  }

  const toggleContent = (
    <>
      {navigationElement}
      <span css={styles.toggleForeground}>
        <h4
          css={styles.toggleLabel}
          style={{ '--toggleFontWeight': boldText ? '600' : '400' } as styles.ToggleFontWeightCSS}
        >
          {labelText}
          {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
        </h4>
        <input
          aria-hidden={isNavigationOnly ? true : undefined}
          checked={active}
          css={styles.hiddenInput}
          disabled={disabled}
          name={name}
          onChange={handleChange}
          tabIndex={isNavigationOnly ? -1 : undefined}
          type="checkbox"
          value={value}
        />

        {/* Visual toggle switch */}
        <div css={styles.toggleSwitch}>
          <span css={styles.toggleSlider}>
            {active && <Icon color="var(--neutral-black-base)" name="checkmark" size="sm" />}
          </span>
        </div>
      </span>
    </>
  );

  if (isNavigationOnly) {
    return (
      <span css={outerStyle}>
        <span css={styles.toggleLabelRoot} tabIndex={-1}>
          {toggleContent}
        </span>
      </span>
    );
  }

  return (
    <span css={outerStyle}>
      <label
        aria-disabled={disabled}
        aria-pressed={active}
        css={styles.toggleLabelRoot}
        onKeyDown={handleKeyDown}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {toggleContent}
      </label>
    </span>
  );
};
